import { Document } from '../components/document-list-vault/types';
import { ROUTES } from '../../../common/constants/routes';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectAllEmployees } from '../../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../../slices/customers/customersSlice';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { selectBusinessProfileContacts } from '../../../slices/business-profile/businessProfileSlice';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';

export const useDocumentsFunctions = () => {
  const location = useLocation();
  const path = location.pathname;
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const backURL = params.get('backURL');
  const getProfileType = (
    profile: string
  ): 'employment' | 'supplier' | 'customer' | 'business' | 'unknown' => {
    const lowerProfile = profile.toLowerCase();
    if (lowerProfile.includes('supplier')) return 'supplier';
    if (lowerProfile.includes('customer')) return 'customer';
    if (lowerProfile.includes('business')) return 'business';
    if (lowerProfile !== '-') return 'employment';
    return 'unknown';
  };

  const getWorkflow = (doc: Document): string => {
    if ('dc_workflows' in doc) {
      return doc.dc_workflows.length > 0 ? doc.dc_workflows.join(', ') : '-';
    } else if ('du_workflows' in doc) {
      return doc.du_workflows.length > 0 ? doc.du_workflows.join(', ') : '-';
    }
  };

  const getDocumentType = (doc: Document): string => {
    if ('dc_types' in doc) {
      return doc.dc_types.length > 0 ? doc.dc_types.join(', ') : '-';
    } else if ('du_types' in doc) {
      return doc.du_types.length > 0 ? doc.du_types.join(', ') : '-';
    }
  };

  const getProfileName = (profileType: string, doc: Document): string[] => {
    switch (profileType) {
      case 'employment':
        return doc.employees.length > 0
          ? doc.employees.map((el) => {
              return employees?.find((item) => item.id === el)?.full_name;
            })
          : ['-'];
      case 'supplier':
        return doc.suppliers.length > 0
          ? doc.suppliers.map((el) => {
              return suppliers?.find((item) => item.id === el)?.name;
            })
          : ['-'];
      case 'customer':
        return doc.customers.length > 0
          ? doc.customers.map((el) => {
              return customers?.find((item) => item.id === el)?.customer_name;
            })
          : ['-'];
      case 'business':
        return doc.business_profile_contacts.length > 0
          ? doc.business_profile_contacts.map((el) => {
              return businessContacts?.find((item) => item.id === el)?.contact_name;
            })
          : ['-'];
      default:
        return ['-'];
    }
  };

  const getFileTypeFromName = (name: string, contract_generation: number) => {
    const lastCharacters = name?.slice(-4);
    if (contract_generation === undefined) {
      if (lastCharacters.includes('.')) {
        const charactersWithoutDot = lastCharacters.replace('.', '');
        return charactersWithoutDot;
      }
      return lastCharacters;
    } else {
      return 'bk';
    }
  };

  const navigateToDocumentPageTab = (
    doc: Document,
    tab: number,
    generateTemplate?: boolean,
    generateSummary?: boolean,
    editDocument?: boolean
  ) => {
    navigate(
      `${
        path.includes(ROUTES.WORKSPACE) ? ROUTES.WORKSPACE + '/document' : ROUTES.DOCUMENT_VAULT
      }/${doc.id}?initialTab=${tab}${
        'contract_generation' in doc ? '&contractGenerated=true' : ''
      }${generateTemplate ? '&generateTemplate=true' : ''}${
        generateSummary ? '&generateSummary=true' : ''
      }${editDocument ? '&editDocument=true' : ''}${
        !(
          location.pathname.includes(ROUTES.WORKSPACE) ||
          location.pathname.includes(ROUTES.DOCUMENT_VAULT)
        ) || backURL
          ? `&backURL=${
              !(
                location.pathname.includes(ROUTES.WORKSPACE) ||
                location.pathname.includes(ROUTES.DOCUMENT_VAULT)
              )
                ? encodeURIComponent(sanitizeRedirectUrl(location.pathname))
                : encodeURIComponent(sanitizeRedirectUrl(backURL))
            }`
          : ''
      }`
    );
  };
  return {
    getProfileType,
    getWorkflow,
    getDocumentType,
    getProfileName,
    getFileTypeFromName,
    navigateToDocumentPageTab,
  };
};
